import { useState, useCallback, useMemo } from 'react'
import { gql } from '@apollo/client'
import * as ApolloReactHooks from '../../legacy/apollo/codegenReactApolloHooks'
import { type FetchCurrentEnterpriseAccountQuery } from '../../__codegen__/api'
import { makeQueryStaleWhileRevalidate } from '../../utils/apollo/makeQueryStaleWhileRevalidate'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * The core account query result — everything from the original
 * FetchCurrentEnterpriseAccount query *except* featureToggles.
 */
export type FetchCurrentEnterpriseAccountCoreQuery = {
  __typename?: 'Query'
  currentEnterpriseAccount: Omit<
    FetchCurrentEnterpriseAccountQuery['currentEnterpriseAccount'],
    'featureToggles'
  >
}

/**
 * The feature toggles query result — only the fields needed for feature toggles.
 */
export type FetchCurrentEnterpriseAccountFeatureTogglesQuery = {
  __typename?: 'Query'
  currentEnterpriseAccount: Pick<
    FetchCurrentEnterpriseAccountQuery['currentEnterpriseAccount'],
    '__typename' | 'id' | 'featureToggles'
  >
}

type EmptyVariables = Record<string, never>

// ---------------------------------------------------------------------------
// GraphQL Documents
// ---------------------------------------------------------------------------

export const FetchCurrentEnterpriseAccountCoreDocument = gql`
  query fetchCurrentEnterpriseAccountCore {
    currentEnterpriseAccount {
      canonicalId
      id
      createdAt
      givenName
      surname
      email
      accountStateValid
      isInternal
      authServiceRequired
      assignedPartnerIds
      assignedRetailerIds
      assignedRetailerLocationIds
      authentication {
        ... on EnterpriseAuthenticationEmailPassword {
          emailVerified
        }
      }
      featureExperiments {
        name
        variant
      }
      policies {
        viewAdminToolsSection
        viewRetailerDashboardSection
      }
      internalAccessConfiguration {
        hasAllPartnersFullAccess
        hasFullPartnerAccess
        hasFullRetailerAccess
        hasNothingAccessible
      }
      roles {
        slug
      }
      permissions {
        slug
      }
      mfaEnabled
      mfaEnabledAt
      mfaDeactivatedAt
      acceptedPartnerAgreements {
        partnerId
        agreementType
      }
      isImpersonating
      hiddenSettings {
        rsatPulseSurvey {
          lastSeenTime
          seenCount
          slug
          hasSubmitted
        }
      }
      userLocale
      canUpdatePartnerWriteAccess
      partnerWriteAccess {
        partnerId
        partner {
          id
          name
        }
      }
      hasAllPartnersWriteAccess
    }
  }
`

export const FetchCurrentEnterpriseAccountFeatureTogglesDocument = gql`
  query fetchCurrentEnterpriseAccountFeatureToggles {
    currentEnterpriseAccount {
      id
      featureToggles {
        name
      }
    }
  }
`

// ---------------------------------------------------------------------------
// Apollo Hooks
// ---------------------------------------------------------------------------

const defaultOptions = {} as const

export function useFetchCurrentEnterpriseAccountCoreLazyQuery(
  baseOptions?: ApolloReactHooks.LazyQueryHookOptions<
    FetchCurrentEnterpriseAccountCoreQuery,
    EmptyVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions }
  return ApolloReactHooks.useLazyQuery<FetchCurrentEnterpriseAccountCoreQuery, EmptyVariables>(
    FetchCurrentEnterpriseAccountCoreDocument,
    options
  )
}

export function useFetchCurrentEnterpriseAccountFeatureTogglesQuery(
  baseOptions?: ApolloReactHooks.QueryHookOptions<
    FetchCurrentEnterpriseAccountFeatureTogglesQuery,
    EmptyVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions }
  return ApolloReactHooks.useQuery<
    FetchCurrentEnterpriseAccountFeatureTogglesQuery,
    EmptyVariables
  >(FetchCurrentEnterpriseAccountFeatureTogglesDocument, options)
}

export function useFetchCurrentEnterpriseAccountCoreQuery(
  baseOptions?: ApolloReactHooks.QueryHookOptions<
    FetchCurrentEnterpriseAccountCoreQuery,
    EmptyVariables
  >
) {
  const options = { ...defaultOptions, ...baseOptions }
  return ApolloReactHooks.useQuery<FetchCurrentEnterpriseAccountCoreQuery, EmptyVariables>(
    FetchCurrentEnterpriseAccountCoreDocument,
    options
  )
}

// ---------------------------------------------------------------------------
// SWR-wrapped hooks
// ---------------------------------------------------------------------------

export const useCachedCoreAccountQuery = makeQueryStaleWhileRevalidate(
  useFetchCurrentEnterpriseAccountCoreQuery,
  { baseCacheKey: 'currentEnterpriseAccountCore' }
)

export const useCachedFeatureTogglesQuery = makeQueryStaleWhileRevalidate(
  useFetchCurrentEnterpriseAccountFeatureTogglesQuery,
  { baseCacheKey: 'currentEnterpriseAccountFeatureToggles' }
)

// ---------------------------------------------------------------------------
// Composite Hook
// ---------------------------------------------------------------------------

export type CurrentAccountSplitLazyQueryOptions = ApolloReactHooks.QueryHookOptions<
  FetchCurrentEnterpriseAccountCoreQuery,
  EmptyVariables
>

/**
 * Composite lazy query hook that splits the enterprise account fetch into two parallel queries:
 *
 * 1. **Core account data** (SWR-cached) — everything except featureToggles, served from
 *    localStorage cache instantly when available, then revalidated in the background
 * 2. **Feature toggles** (SWR-cached) — served from localStorage cache instantly when available,
 *    then revalidated in the background
 *
 * Returns a tuple matching the shape of `useFetchCurrentEnterpriseAccountLazyQuery`
 * so it's a drop-in replacement. Both queries must resolve before `loading` becomes `false`.
 *
 * The `triggered` gate ensures no cached data leaks through before session validation.
 */
export function useCurrentAccountSplitLazyQuery(options?: CurrentAccountSplitLazyQueryOptions) {
  const [triggered, setTriggered] = useState(false)

  // Core account data — SWR-cached, fires when triggered
  const coreResult = useCachedCoreAccountQuery({ ...options, skip: !triggered })

  // Feature toggles — SWR-cached, fires when triggered
  const featureTogglesResult = useCachedFeatureTogglesQuery({ skip: !triggered })

  // Single trigger function that starts both queries
  const fetchAccount = useCallback(() => {
    setTriggered(true)
  }, [])

  // Merge core data + feature toggles into the original FetchCurrentEnterpriseAccountQuery shape.
  // Gate on `triggered` to prevent cached data from leaking before session validation.
  const mergedData = useMemo<FetchCurrentEnterpriseAccountQuery | undefined>(() => {
    if (!triggered) return undefined

    const coreAccount = coreResult.data?.currentEnterpriseAccount
    if (!coreAccount) return undefined

    return {
      currentEnterpriseAccount: {
        ...coreAccount,
        featureToggles: featureTogglesResult.data?.currentEnterpriseAccount?.featureToggles ?? [],
      },
    } as FetchCurrentEnterpriseAccountQuery
  }, [triggered, coreResult.data, featureTogglesResult.data])

  // Both must resolve before the hook reports not-loading.
  // SWR resolves instantly from cache when available, so this rarely blocks.
  const featureTogglesLoading = triggered && featureTogglesResult.loading
  const loading = coreResult.loading || featureTogglesLoading

  return [
    fetchAccount,
    {
      data: mergedData,
      loading,
      error: coreResult.error,
      called: triggered,
      refetch: coreResult.refetch,
    },
  ] as const
}
